import React, {useCallback, useRef, useState} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Button from "@amzn/meridian/button";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {createSortPathConfiguration} from "../../resources/SortPathSettingsResource";
import {getNodeId} from "../../Utility";
import {
    parseCSV,
    parseClusterParamsCSV,
    validateRow,
    buildSortPathSettingFromRow,
} from "../../helper/BulkSortGridAllocationHelper";
import { CSV_HEADERS } from "../../constants/BulkUploadConstants";

const ProgressBar = ({current, total}) => {
    const percent = total > 0 ? Math.round((current / total) * 100) : 0;
    return (
        <Column spacing="xsmall">
            <Row alignmentHorizontal="center">
                <Text type="b300">Uploading {current}/{total}</Text>
            </Row>
            <div style={{width: "100%", backgroundColor: "#e0e0e0", borderRadius: "4px", height: "8px"}}>
                <div style={{
                    width: `${percent}%`,
                    backgroundColor: "#0073bb",
                    height: "8px",
                    borderRadius: "4px",
                    transition: "width 0.3s ease",
                }}/>
            </div>
        </Column>
    );
};

const BulkSortGridAllocationUpload = ({intl, onComplete, sortPathConfigurationList}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [uploadResults, setUploadResults] = useState([]);
    const [parsedRows, setParsedRows] = useState([]);
    const [rawRows, setRawRows] = useState([]);
    const [clusterParamsMap, setClusterParamsMap] = useState(null);
    const [mainFileName, setMainFileName] = useState("");
    const [clusterFileName, setClusterFileName] = useState("");
    const [isValidated, setIsValidated] = useState(false);
    const [parseErrors, setParseErrors] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({current: 0, total: 0});
    const mainFileInputRef = useRef(null);
    const clusterFileInputRef = useRef(null);

    const resetState = useCallback(() => {
        setValidationErrors([]);
        setUploadResults([]);
        setParsedRows([]);
        setRawRows([]);
        setClusterParamsMap(null);
        setMainFileName("");
        setClusterFileName("");
        setIsUploading(false);
        setIsValidated(false);
        setParseErrors([]);
        setUploadProgress({current: 0, total: 0});
        if (mainFileInputRef.current) mainFileInputRef.current.value = "";
        if (clusterFileInputRef.current) clusterFileInputRef.current.value = "";
    }, []);

    const onCloseModal = useCallback(() => {
        if (isUploading) return;
        setIsModalOpen(false);
        if (uploadResults.some(r => r.success)) {
            onComplete();
        }
        resetState();
    }, [isUploading, uploadResults, onComplete, resetState]);

    const handleMainFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        setParseErrors([]);
        setValidationErrors([]);
        setUploadResults([]);
        setParsedRows([]);
        setRawRows([]);
        setIsValidated(false);
        setMainFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            const result = parseCSV(e.target.result);
            if (result.error) {
                setParseErrors([result.error]);
                return;
            }
            setRawRows(result.rows);
        };
        reader.readAsText(file);
    }, []);

    const handleClusterFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        setParseErrors(prev => prev.filter(e => !e.startsWith('Cluster params:')));
        setClusterFileName(file.name);
        setIsValidated(false);
        const reader = new FileReader();
        reader.onload = (e) => {
            const result = parseClusterParamsCSV(e.target.result);
            if (result.error) {
                setParseErrors(prev => [...prev, `Cluster params: ${result.error}`]);
                return;
            }
            setClusterParamsMap(result.clusterParamsMap);
        };
        reader.readAsText(file);
    }, []);

    const handleValidate = useCallback(() => {
        const errors = [];
        rawRows.forEach(row => {
            const error = validateRow(row, clusterParamsMap, sortPathConfigurationList);
            if (error) errors.push(error);
        });

        if (errors.length > 0) {
            setValidationErrors(errors);
            setIsValidated(false);
        } else {
            setValidationErrors([]);
            setParsedRows(rawRows);
            setIsValidated(true);
        }
    }, [rawRows, clusterParamsMap, sortPathConfigurationList]);

    const handleUpload = useCallback(async () => {
        setIsUploading(true);
        setUploadProgress({current: 0, total: parsedRows.length});
        const results = [];

        for (let i = 0; i < parsedRows.length; i++) {
            const row = parsedRows[i];
            setUploadProgress({current: i + 1, total: parsedRows.length});
            const sortPathSetting = buildSortPathSettingFromRow(row, clusterParamsMap, sortPathConfigurationList);
            const isUpdate = !!sortPathSetting.settingId;
            try {
                const response = await createSortPathConfiguration(getNodeId(), sortPathSetting);
                if (response && response.sortPathSetting) {
                    results.push({
                        row: row._rowNumber,
                        name: sortPathSetting.name,
                        cycle: row.cycle,
                        success: true,
                        isUpdate,
                    });
                } else {
                    var errorMsg;
                    try {
                        errorMsg = JSON.parse(response.errorMessage).message;
                    } catch (_) {
                        errorMsg = response.errorMessage || "Unknown error";
                    }
                    results.push({
                        row: row._rowNumber,
                        name: sortPathSetting.name,
                        cycle: row.cycle,
                        success: false,
                        isUpdate,
                        error: errorMsg,
                    });
                }
            } catch (error) {
                results.push({
                    row: row._rowNumber,
                    name: sortPathSetting.name,
                    cycle: row.cycle,
                    success: false,
                    isUpdate,
                    error: error.toString(),
                });
            }
        }

        setUploadResults(results);
        setIsUploading(false);
    }, [parsedRows, clusterParamsMap, sortPathConfigurationList]);

    const successCount = uploadResults.filter(r => r.success).length;
    const failureCount = uploadResults.filter(r => !r.success).length;

    return (
        <>
            <Button onClick={() => setIsModalOpen(true)}>
                <FormattedMessage id="sortPaths.bulkUpload" defaultMessage="Bulk Upload CSV"/>
            </Button>

            <Modal
                title={intl.formatMessage({id: "sortPaths.bulkUploadTitle", defaultMessage: "Bulk Upload Sort Grid Allocations"})}
                open={isModalOpen}
                onClose={onCloseModal}
                closable={!isUploading}
                width="1000px"
                scrollContainer="modal"
            >
                <Column spacing="medium">
                    {/* File inputs */}
                    {uploadResults.length === 0 && (
                        <Column spacing="small">
                            <Text type="b300">
                                <FormattedMessage id="sortPaths.bulkUploadInstructions"
                                    defaultMessage="Upload a main configuration CSV. Optionally, upload a cluster parameters CSV with per-cluster settings."/>
                            </Text>

                            {/* Main CSV */}
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.mainCsvLabel" defaultMessage="Main Configuration CSV (required)"/>
                                </Text>
                                <input
                                    ref={mainFileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleMainFileSelect}
                                    data-testid="bulkUploadMainFileInput"
                                />
                                {mainFileName && <Text type="b200" color="secondary">{mainFileName}</Text>}
                            </Column>

                            {/* Cluster params CSV */}
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.clusterCsvLabel"
                                        defaultMessage="Cluster Parameters CSV (for STAG and AMZL sites)"/>
                                </Text>
                                <input
                                    ref={clusterFileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleClusterFileSelect}
                                    data-testid="bulkUploadClusterFileInput"
                                />
                                {clusterFileName && <Text type="b200" color="secondary">{clusterFileName}</Text>}
                            </Column>
                        </Column>
                    )}

                    {/* Parse errors */}
                    {parseErrors.length > 0 && (
                        <Alert type="error">
                            <Column spacing="xsmall">
                                {parseErrors.map((err, i) => (
                                    <Text key={i} type="b200">{err}</Text>
                                ))}
                            </Column>
                        </Alert>
                    )}

                    {/* Validation errors */}
                    {validationErrors.length > 0 && (
                        <Alert type="error">
                            <Column spacing="xsmall">
                                <Text type="h100">
                                    <FormattedMessage id="sortPaths.bulkValidationErrors" defaultMessage="Validation errors found:"/>
                                </Text>
                                {validationErrors.map((err, i) => (
                                    <Text key={i} type="b200">{err}</Text>
                                ))}
                            </Column>
                        </Alert>
                    )}

                    {/* Progress bar during upload — replaces the info alert */}
                    {isUploading && (
                        <ProgressBar current={uploadProgress.current} total={uploadProgress.total}/>
                    )}

                    {/* Parsed rows preview */}
                    {isValidated && parsedRows.length > 0 && uploadResults.length === 0 && (
                        <Column spacing="small">
                            {!isUploading && (
                                <Alert type="success">
                                    <FormattedMessage id="sortPaths.bulkRowsParsed"
                                        defaultMessage="{count} configuration(s) validated.{clusterInfo} Click Upload to proceed."
                                        values={{
                                            count: parsedRows.length,
                                            clusterInfo: clusterParamsMap
                                                ? ` Cluster params loaded for ${Object.keys(clusterParamsMap).length} cycle(s).`
                                                : "",
                                        }}/>
                                </Alert>
                            )}
                            <Table headerRows={1} showDividers>
                                <TableRow>
                                    <TableCell>Row</TableCell>
                                    <TableCell>Cycle</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Fill Algorithm</TableCell>
                                    <TableCell>Pick Algorithm</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                                {parsedRows.map(row => {
                                    const hasExisting = sortPathConfigurationList &&
                                        sortPathConfigurationList.some(c => c.nodeId === row[CSV_HEADERS.NODE_ID] && c.cycle === row[CSV_HEADERS.CYCLE]);
                                    return (
                                        <TableRow key={row._rowNumber}>
                                            <TableCell>{row._rowNumber}</TableCell>
                                            <TableCell>{row[CSV_HEADERS.CYCLE]}</TableCell>
                                            <TableCell>{row[CSV_HEADERS.CYCLE] + "_" + row[CSV_HEADERS.FILL_ALGORITHM]}</TableCell>
                                            <TableCell>{row[CSV_HEADERS.FILL_ALGORITHM]}</TableCell>
                                            <TableCell>{row[CSV_HEADERS.PICK_ALGORITHM]}</TableCell>
                                            <TableCell>
                                                <span style={{color: hasExisting ? "#eb5f07" : "#0073bb", fontWeight: "bold"}}>
                                                    {hasExisting ? "UPDATE" : "CREATE"}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        </Column>
                    )}

                    {/* Upload results */}
                    {uploadResults.length > 0 && (
                        <Column spacing="small">
                            <Alert type={failureCount === 0 ? "success" : "warning"}>
                                <FormattedMessage id="sortPaths.bulkUploadSummary"
                                    defaultMessage="{success} succeeded, {failure} failed out of {total} configurations."
                                    values={{success: successCount, failure: failureCount, total: uploadResults.length}}/>
                            </Alert>
                            <Table headerRows={1} showDividers>
                                <TableRow>
                                    <TableCell>Row</TableCell>
                                    <TableCell>Cycle</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Error Details</TableCell>
                                </TableRow>
                                {uploadResults.map(result => (
                                    <TableRow key={result.row}>
                                        <TableCell>{result.row}</TableCell>
                                        <TableCell>{result.cycle}</TableCell>
                                        <TableCell>{result.name}</TableCell>
                                        <TableCell>
                                            <span style={{color: result.isUpdate ? "#eb5f07" : "#0073bb", fontWeight: "bold"}}>
                                                {result.isUpdate ? "UPDATE" : "CREATE"}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span style={{color: result.success ? "#1d8102" : "#d13212", fontWeight: "bold"}}>
                                                {result.success ? "SUCCESS" : "FAILED"}
                                            </span>
                                        </TableCell>
                                        <TableCell>{result.error || "-"}</TableCell>
                                    </TableRow>
                                ))}
                            </Table>
                        </Column>
                    )}
                </Column>

                <ModalFooter>
                    <Row alignmentHorizontal="end" spacing="small">
                        <Button type="secondary" onClick={onCloseModal}>
                            <FormattedMessage id="sortPaths.close" defaultMessage="Close"/>
                        </Button>
                        {rawRows.length > 0 && !isValidated && uploadResults.length === 0 && (
                            <Button onClick={handleValidate}>
                                <FormattedMessage id="sortPaths.validate" defaultMessage="Validate"/>
                            </Button>
                        )}
                        {isValidated && parsedRows.length > 0 && uploadResults.length === 0 && !isUploading && (
                            <Button onClick={handleUpload} disabled={isUploading}>
                                <FormattedMessage id="sortPaths.upload" defaultMessage="Upload"/>
                            </Button>
                        )}
                    </Row>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default injectIntl(BulkSortGridAllocationUpload);
